import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  FileText,
  Download,
  Calendar,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  Briefcase,
  BarChart3,
  PieChart
} from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';

const Reports = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const [activeReport, setActiveReport] = useState('sales');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0], // First day of month
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  const [selectedTechnician, setSelectedTechnician] = useState('all');

  const reportTypes = [
    { id: 'sales', name: 'Sales Report', icon: TrendingUp, color: 'green' },
    { id: 'purchase', name: 'Purchase Report', icon: Package, color: 'blue' },
    { id: 'inventory', name: 'Inventory Report', icon: Package, color: 'purple' },
    { id: 'jobs', name: 'Job Report', icon: Briefcase, color: 'orange' },
    { id: 'technicians', name: 'Technician Report', icon: Users, color: 'cyan' },
    { id: 'expenses', name: 'Expense Report', icon: DollarSign, color: 'red' },
    { id: 'customers', name: 'Customer Ledger', icon: Users, color: 'indigo' },
    { id: 'profit-loss', name: 'Profit & Loss', icon: BarChart3, color: 'emerald' }
  ];

  // Fetch report data based on active report
  const { data: reportData, isLoading } = useQuery(
    ['report', activeReport, dateRange, selectedBranch?.id],
    async () => {
      const params = {
        branchId: selectedBranch?.id,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        technicianName: activeReport === 'technicians' ? selectedTechnician : undefined
      };
      const response = await api.get(`/api/reports/${activeReport}`, { params });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch
    }
  );

  const handleExport = (format) => {
    if (!reportData) {
      toast.error('No data to export');
      return;
    }

    if (format === 'excel') {
      exportToExcel();
    } else if (format === 'pdf') {
      exportToPDF();
    }
  };

  const exportToExcel = () => {
    try {
      const reportName = reportTypes.find(r => r.id === activeReport)?.name || 'Report';
      const rows = [];
      
      // Title and header
      rows.push([reportName.toUpperCase()]);
      rows.push([]);
      rows.push(['Branch:', selectedBranch?.name || 'All Branches']);
      rows.push(['Period:', `${dateRange.startDate} to ${dateRange.endDate}`]);
      rows.push(['Generated:', new Date().toLocaleString()]);
      rows.push([]);

      // Add data based on report type
      if (activeReport === 'sales') {
        const sales = reportData?.sales || [];
        const summary = reportData?.summary || {};
        
        // Summary section
        rows.push(['SUMMARY']);
        rows.push(['Total Sales:', summary.totalSales?.toFixed(2) || '0.00']);
        rows.push(['Total Quantity:', summary.totalQuantity || 0]);
        rows.push(['Total Profit:', summary.totalProfit?.toFixed(2) || '0.00']);
        rows.push([]);
        
        // Data table
        rows.push(['Date', 'Product', 'Quantity', 'Rate', 'Amount']);
        sales.forEach(sale => {
          rows.push([
            new Date(sale.date).toLocaleDateString(),
            sale.productName,
            sale.quantity,
            sale.rate.toFixed(2),
            sale.amount.toFixed(2)
          ]);
        });
        
      } else if (activeReport === 'purchase') {
        const purchases = reportData?.purchases || [];
        const summary = reportData?.summary || {};
        
        rows.push(['SUMMARY']);
        rows.push(['Total Purchase:', summary.totalPurchase?.toFixed(2) || '0.00']);
        rows.push(['Total Quantity:', summary.totalQuantity || 0]);
        rows.push([]);
        
        rows.push(['Date', 'Supplier', 'Product', 'Quantity', 'Amount', 'Payment Mode']);
        purchases.forEach(purchase => {
          rows.push([
            new Date(purchase.date).toLocaleDateString(),
            purchase.supplierName,
            purchase.productName,
            purchase.quantity,
            purchase.amount.toFixed(2),
            purchase.paymentMode || 'N/A'
          ]);
        });
        
      } else if (activeReport === 'inventory') {
        const inventory = reportData?.inventory || [];
        const summary = reportData?.summary || {};
        
        rows.push(['SUMMARY']);
        rows.push(['Total Value:', summary.totalValue?.toFixed(2) || '0.00']);
        rows.push(['Low Stock Items:', summary.lowStockItems || 0]);
        rows.push([]);
        
        rows.push(['Product', 'Current Stock', 'Min Limit', 'Rate', 'Value']);
        inventory.forEach(item => {
          rows.push([
            item.productName,
            item.currentStock,
            item.minStockLimit,
            item.rate.toFixed(2),
            item.value.toFixed(2)
          ]);
        });
        
      } else if (activeReport === 'jobs') {
        const jobs = reportData?.jobs || [];
        const summary = reportData?.summary || {};
        
        rows.push(['SUMMARY']);
        rows.push(['Total Jobs:', summary.totalJobs || 0]);
        rows.push(['Completed:', summary.completed || 0]);
        rows.push(['Pending:', summary.pending || 0]);
        rows.push(['Total Revenue:', summary.totalRevenue?.toFixed(2) || '0.00']);
        rows.push([]);
        
        rows.push(['Job Number', 'Customer', 'Device', 'Status', 'Amount']);
        jobs.forEach(job => {
          rows.push([
            job.jobNumber,
            job.customerName,
            `${job.deviceBrand} ${job.deviceModel}`,
            job.status,
            (job.actualCost || job.estimatedCost).toFixed(2)
          ]);
        });
        
      } else if (activeReport === 'expenses') {
        const expenses = reportData?.expenses || [];
        const summary = reportData?.summary || {};
        
        rows.push(['SUMMARY']);
        rows.push(['Total Expenses:', summary.totalExpense?.toFixed(2) || '0.00']);
        rows.push([]);
        
        rows.push(['Date', 'Title', 'Category', 'Payment Mode', 'Amount']);
        expenses.forEach(expense => {
          rows.push([
            new Date(expense.date).toLocaleDateString(),
            expense.title,
            expense.category,
            expense.paymentMode,
            expense.amount.toFixed(2)
          ]);
        });
        
      } else if (activeReport === 'customers') {
        const customers = reportData?.customers || [];
        const summary = reportData?.summary || {};
        
        rows.push(['SUMMARY']);
        rows.push(['Total Customers:', summary.totalCustomers || 0]);
        rows.push(['Outstanding Balance:', summary.totalOutstanding?.toFixed(2) || '0.00']);
        rows.push([]);
        
        rows.push(['Customer Name', 'Phone', 'Total Jobs', 'Balance Due']);
        customers.forEach(customer => {
          rows.push([
            customer.name,
            customer.phone,
            customer.totalJobs,
            customer.balance.toFixed(2)
          ]);
        });
        
      } else if (activeReport === 'profit-loss') {
        const summary = reportData?.summary || {};
        
        rows.push(['INCOME / REVENUE']);
        rows.push(['Labour Revenue:', summary.labourRevenue?.toFixed(2) || '0.00']);
        rows.push(['Spare Sales Revenue:', summary.spareRevenue?.toFixed(2) || '0.00']);
        rows.push(['Total Gross Revenue:', summary.totalRevenue?.toFixed(2) || '0.00']);
        rows.push([]);
        
        rows.push(['COSTS & EXPENSES']);
        rows.push(['COGS (Parts Cost):', summary.totalCOGS?.toFixed(2) || '0.00']);
        rows.push(['Operating Expenses:', summary.totalExpenses?.toFixed(2) || '0.00']);
        rows.push([]);
        
        rows.push(['NET RESULTS']);
        rows.push(['Gross Margin:', summary.grossProfit?.toFixed(2) || '0.00']);
        rows.push(['Net Profit/Loss:', summary.netProfit?.toFixed(2) || '0.00']);
      } else if (activeReport === 'technicians') {
        const technicians = reportData?.technicians || [];
        const summary = reportData?.summary || {};

        rows.push(['SUMMARY']);
        rows.push(['Total Technicians:', summary.totalTechnicians || 0]);
        rows.push(['Total Jobs:', summary.totalJobs || 0]);
        rows.push(['Total Revenue:', summary.totalRevenue?.toFixed(2) || '0.00']);
        rows.push([]);

        technicians.forEach(tech => {
          rows.push([`TECHNICIAN: ${tech.name.toUpperCase()}`]);
          rows.push(['Total Jobs', 'Completed', 'Pending', 'Returned', 'Revenue']);
          rows.push([
            tech.totalJobs,
            tech.completed,
            tech.pending,
            tech.returned,
            tech.totalRevenue.toFixed(2)
          ]);
          rows.push([]);
          rows.push(['Job Number', 'Date', 'Customer', 'Device', 'Status', 'Amount']);
          tech.jobs.forEach(job => {
            rows.push([
              job.jobNumber,
              new Date(job.date).toLocaleDateString(),
              job.customerName,
              job.device,
              job.status,
              job.amount.toFixed(2)
            ]);
          });
          rows.push([]);
          rows.push([]);
        });
      }

      // Convert to CSV with proper escaping
      const csvContent = rows.map(row => 
        row.map(cell => {
          const cellStr = String(cell || '');
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');

      // Add BOM for proper UTF-8 encoding in Excel
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const fileName = `${activeReport}_report_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const exportToPDF = () => {
    const element = document.querySelector('.report-print-container');
    if (!element) return;

    const runDownload = () => {
      const opt = {
        margin:       10,
        filename:     `Report_${activeReport}_${dateRange.startDate}_to_${dateRange.endDate}.pdf`,
        image:        { type: 'jpeg', quality: 0.98 },
        html2canvas:  { scale: 2, useCORS: true, allowTaint: true, logging: false, scrollX: 0, scrollY: 0 },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      // 1. Save state and temporarily modify styles inline
      const prevStyle = element.getAttribute('style') || '';
      element.style.cssText = `
        visibility: visible !important;
        position: relative !important;
        left: auto !important;
        top: auto !important;
        width: 190mm !important;
        background: white !important;
        color: black !important;
        display: block !important;
        margin: 0 auto !important;
        z-index: 9999 !important;
        padding: 20px !important;
        box-shadow: none !important;
        border: none !important;
      `;

      // 2. Hide "no-print" sections in the original element
      const noPrintElements = element.querySelectorAll('.no-print');
      const prevNoPrintStyles = [];
      noPrintElements.forEach((el, idx) => {
        prevNoPrintStyles[idx] = el.getAttribute('style') || '';
        el.style.setProperty('display', 'none', 'important');
      });

      // 3. Make the hidden print header visible
      const printHeader = element.querySelector('.print-header-pdf');
      let prevPrintHeaderStyle = '';
      if (printHeader) {
        prevPrintHeaderStyle = printHeader.getAttribute('style') || '';
        printHeader.style.setProperty('display', 'block', 'important');
      }

      // 4. Wait for any images to load
      const allImages = Array.from(element.querySelectorAll('img'));
      const imagePromises = allImages.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(res => {
          img.onload = res;
          img.onerror = res;
        });
      });

      toast.loading('Generating Report PDF...', { id: 'pdf-toast' });

      Promise.all(imagePromises).then(() => {
        requestAnimationFrame(() => {
          window.html2pdf().from(element).set(opt).save()
            .then(() => {
              // Restore states
              element.setAttribute('style', prevStyle);
              noPrintElements.forEach((el, idx) => {
                el.setAttribute('style', prevNoPrintStyles[idx]);
              });
              if (printHeader) {
                printHeader.setAttribute('style', prevPrintHeaderStyle);
              }
              toast.dismiss('pdf-toast');
              toast.success('Report PDF downloaded successfully!');
            })
            .catch((err) => {
              console.error(err);
              element.setAttribute('style', prevStyle);
              noPrintElements.forEach((el, idx) => {
                el.setAttribute('style', prevNoPrintStyles[idx]);
              });
              if (printHeader) {
                printHeader.setAttribute('style', prevPrintHeaderStyle);
              }
              toast.dismiss('pdf-toast');
              toast.error('Failed to generate PDF. Opening print dialog as fallback.');
              window.print();
            });
        });
      });
    };

    if (!window.html2pdf) {
      toast.loading('Loading PDF library...', { id: 'pdf-toast' });
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => {
        toast.dismiss('pdf-toast');
        runDownload();
      };
      script.onerror = () => {
        toast.dismiss('pdf-toast');
        toast.error('Failed to load PDF library. Opening print dialog instead.');
        window.print();
      };
      document.head.appendChild(script);
    } else {
      runDownload();
    }
  };

  return (
    <>
      {/* Print Styles for PDF Export */}
      <style>{`
        .print-header-pdf {
          display: none !important;
        }
        @media print {
          @page {
            margin: 15mm 10mm 15mm 10mm !important;
          }
          .print-header-pdf {
            display: block !important;
          }
          /* Hide sidebar, navigation, and everything else */
          nav, sidebar, header, .sidebar-container, .navbar-container, footer {
            display: none !important;
          }
          .no-print {
            display: none !important;
          }
          
          /* Optimize layout for printing */
          body {
            background: white !important;
            color: black !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .space-y-6 {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .card {
            box-shadow: none !important;
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
          }
          
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          
          th, td {
            border: 1px solid #e5e7eb !important;
            color: black !important;
            padding: 8px !important;
          }
          
          /* Ensure colors print correctly */
          .bg-green-50, .bg-blue-50, .bg-purple-50, .bg-red-50, .bg-orange-50 {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
      
    <div className="space-y-6 report-print-container">
      {/* PDF Export Header - Only visible when generating PDF or printing */}
      <div className="hidden print-header-pdf mb-6 border-b pb-4">
        <h2 className="text-xl font-bold text-gray-900 uppercase tracking-wide">
          {reportTypes.find(r => r.id === activeReport)?.name || 'Report'}
        </h2>
        <p className="text-sm text-gray-600">Branch: {selectedBranch?.name || 'All Branches'}</p>
        <p className="text-sm text-gray-600">
          Period: {dateRange.startDate} to {dateRange.endDate}
        </p>
        <p className="text-sm text-gray-650">Generated: {new Date().toLocaleString()}</p>
      </div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between no-print">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="w-7 h-7 mr-3 text-blue-600 no-print" />
            Reports & Analytics
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Generate comprehensive business reports
          </p>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card no-print">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:items-center gap-4">
            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="input-field"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              />
            </div>
            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="input-field"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
            {activeReport === 'technicians' && (
              <div>
                <label className="form-label">Technician</label>
                <select
                  className="input-field"
                  value={selectedTechnician}
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                >
                  <option value="all">All Technicians</option>
                  {reportData?.technicianList?.map(tech => (
                    <option key={tech} value={tech}>{tech}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 no-print">
            <button
              onClick={() => handleExport('pdf')}
              className="btn-secondary inline-flex items-center justify-center flex-1 sm:flex-none"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="btn-secondary inline-flex items-center justify-center flex-1 sm:flex-none"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* Report Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 no-print">{reportTypes.map((report) => {
          const Icon = report.icon;
          const isActive = activeReport === report.id;
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id)}
              className={`
                p-6 rounded-lg border-2 transition-all text-left
                ${isActive
                  ? `border-${report.color}-500 bg-${report.color}-50`
                  : 'border-gray-200 bg-white hover:border-gray-300'
                }
              `}
            >
              <div className={`
                inline-flex p-3 rounded-lg mb-3
                ${isActive ? `bg-${report.color}-100` : 'bg-gray-100'}
              `}>
                <Icon className={`w-6 h-6 ${isActive ? `text-${report.color}-600` : 'text-gray-600'}`} />
              </div>
              <h3 className={`font-semibold ${isActive ? `text-${report.color}-900` : 'text-gray-900'}`}>
                {report.name}
              </h3>
            </button>
          );
        })}
      </div>

      {/* Print Header - Only visible when printing */}
      <div className="hidden print:block mb-6">
        <h2 className="text-xl font-bold text-gray-900">
          {reportTypes.find(r => r.id === activeReport)?.name || 'Report'}
        </h2>
        <p className="text-sm text-gray-600">
          Branch: {selectedBranch?.name || 'All Branches'}
        </p>
        <p className="text-sm text-gray-600">
          Period: {dateRange.startDate} to {dateRange.endDate}
        </p>
        <p className="text-sm text-gray-600">
          Generated: {new Date().toLocaleString()}
        </p>
      </div>

      {/* Report Content */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : (
          <div>
            {activeReport === 'sales' && <SalesReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'purchase' && <PurchaseReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'inventory' && <InventoryReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'jobs' && <JobReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'expenses' && <ExpenseReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'customers' && <CustomerLedger data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'profit-loss' && <ProfitLossReport data={reportData} currencySymbol={currencySymbol} />}
            {activeReport === 'technicians' && <TechnicianReport data={reportData} currencySymbol={currencySymbol} />}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

// Sales Report Component
const SalesReport = ({ data, currencySymbol }) => {
  const sales = data?.sales || [];
  const summary = {
    totalSales: data?.summary?.totalSales || 0,
    totalQuantity: data?.summary?.totalQuantity || 0,
    totalProfit: data?.summary?.totalProfit || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-green-600 font-medium">Total Sales</p>
          <p className="text-2xl font-bold text-green-900">{currencySymbol}{summary.totalSales.toFixed(2)}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600 font-medium">Total Quantity</p>
          <p className="text-2xl font-bold text-blue-900">{summary.totalQuantity}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600 font-medium">Total Profit</p>
          <p className="text-2xl font-bold text-purple-900">{currencySymbol}{summary.totalProfit.toFixed(2)}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sales.map((sale, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(sale.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{sale.productName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{sale.quantity}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{currencySymbol}{sale.rate.toFixed(2)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {currencySymbol}{sale.amount.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sales.length === 0 && (
        <div className="text-center py-12">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No sales data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

// Purchase Report Component
const PurchaseReport = ({ data, currencySymbol }) => {
  const purchases = data?.purchases || [];
  const summary = {
    totalPurchase: data?.summary?.totalPurchase || 0,
    totalQuantity: data?.summary?.totalQuantity || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600 font-medium">Total Purchase</p>
          <p className="text-2xl font-bold text-blue-900">{currencySymbol}{summary.totalPurchase.toFixed(2)}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600 font-medium">Total Quantity</p>
          <p className="text-2xl font-bold text-purple-900">{summary.totalQuantity}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {purchases.map((purchase, index) => {
              const isReturn = purchase.type === 'PURCHASE_RETURN';
              return (
                <tr key={index} className={isReturn ? 'bg-red-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(purchase.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{purchase.supplierName}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{purchase.productName}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isReturn ? 'text-red-600' : 'text-gray-900'}`}>{purchase.quantity}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${isReturn ? 'text-red-600' : 'text-gray-905'}`}>
                    {currencySymbol}{purchase.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      purchase.paymentMode === 'CREDIT' 
                        ? 'bg-orange-100 text-orange-800' 
                        : isReturn
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {purchase.paymentMode}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {purchases.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No purchase data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

// Inventory Report Component
const InventoryReport = ({ data, currencySymbol }) => {
  const inventory = data?.inventory || [];
  const summary = {
    totalValue: data?.summary?.totalValue || 0,
    lowStockItems: data?.summary?.lowStockItems || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-purple-600 font-medium">Total Inventory Value</p>
          <p className="text-2xl font-bold text-purple-900">{currencySymbol}{summary.totalValue.toFixed(2)}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-sm text-red-600 font-medium">Low Stock Items</p>
          <p className="text-2xl font-bold text-red-900">{summary.lowStockItems}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Limit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {inventory.map((item, index) => (
              <tr key={index} className={item.currentStock <= item.minStockLimit ? 'bg-red-50' : ''}>
                <td className="px-6 py-4 text-sm text-gray-900">{item.productName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.currentStock}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.minStockLimit}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{currencySymbol}{item.rate.toFixed(2)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {currencySymbol}{item.value.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {inventory.length === 0 && (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No inventory data available</p>
        </div>
      )}
    </div>
  );
};

// Job Report Component
const JobReport = ({ data, currencySymbol }) => {
  const jobs = data?.jobs || [];
  const summary = {
    totalJobs: data?.summary?.totalJobs || 0,
    completed: data?.summary?.completed || 0,
    pending: data?.summary?.pending || 0,
    returned: data?.summary?.returned || 0,
    totalRevenue: data?.summary?.totalRevenue || 0,
    totalAdvance: data?.summary?.totalAdvance || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-blue-600 font-medium">Total Jobs</p>
          <p className="text-2xl font-bold text-blue-900">{summary.totalJobs}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-green-600 font-medium">Completed</p>
          <p className="text-2xl font-bold text-green-900">{summary.completed}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-orange-600 font-medium">Pending</p>
          <p className="text-2xl font-bold text-orange-900">{summary.pending}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-red-600 font-medium">Returned</p>
          <p className="text-2xl font-bold text-red-900">{summary.returned}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-purple-600 font-medium">Revenue</p>
          <p className="text-2xl font-bold text-purple-900">{currencySymbol}{summary.totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-emerald-50 p-4 rounded-lg col-span-1">
          <p className="text-sm text-emerald-600 font-medium">Advances</p>
          <p className="text-2xl font-bold text-emerald-900">{currencySymbol}{summary.totalAdvance.toFixed(2)}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">No & Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device & Technician</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Advance</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {jobs.map((job, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{job.jobNumber}</div>
                  <div className="text-xs text-gray-500">{new Date(job.date).toLocaleDateString()}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{job.customerName}</td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{job.device}</div>
                  <div className="text-xs text-blue-600">Tech: {job.technician}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    job.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                    job.status === 'PENDING' ? 'bg-red-100 text-red-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {job.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                  {currencySymbol}{job.advance?.toFixed(2) || '0.00'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right text-gray-900">
                  {currencySymbol}{(job.actualCost || job.estimatedCost).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {jobs.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No job data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

// Expense Report Component
const ExpenseReport = ({ data, currencySymbol }) => {
  const expenses = data?.expenses || [];
  const summary = {
    totalExpense: data?.summary?.totalExpense || 0,
    byCategory: data?.summary?.byCategory || {}
  };

  return (
    <div className="space-y-6">
      <div className="bg-red-50 p-4 rounded-lg">
        <p className="text-sm text-red-600 font-medium">Total Expenses</p>
        <p className="text-2xl font-bold text-red-900">{currencySymbol}{summary.totalExpense.toFixed(2)}</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {expenses.map((expense, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {new Date(expense.date).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{expense.title}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.category}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{expense.paymentMode}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                  {currencySymbol}{expense.amount.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {expenses.length === 0 && (
        <div className="text-center py-12">
          <DollarSign className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No expense data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

// Customer Ledger Component
const CustomerLedger = ({ data, currencySymbol }) => {
  const customers = data?.customers || [];
  const summary = {
    totalCustomers: data?.summary?.totalCustomers || 0,
    customersWithBalance: data?.summary?.customersWithBalance || 0,
    totalOutstanding: data?.summary?.totalOutstanding || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-indigo-50 p-4 rounded-lg">
          <p className="text-sm text-indigo-600 font-medium">Total Customers</p>
          <p className="text-2xl font-bold text-indigo-900">{summary.totalCustomers}</p>
        </div>
        <div className="bg-orange-50 p-4 rounded-lg">
          <p className="text-sm text-orange-600 font-medium">Customers with Balance</p>
          <p className="text-2xl font-bold text-orange-900">{summary.customersWithBalance}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <p className="text-sm text-red-600 font-medium">Total Outstanding</p>
          <p className="text-2xl font-bold text-red-900">{currencySymbol}{summary.totalOutstanding.toFixed(2)}</p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Jobs</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance Due</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {customers.map((customer, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.phone}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">{customer.totalJobs}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${customer.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {currencySymbol}{customer.balance.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {customers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No customer data available</p>
        </div>
      )}
    </div>
  );
};

// Profit & Loss Report Component
const ProfitLossReport = ({ data, currencySymbol }) => {
  const summary = {
    totalRevenue: data?.summary?.totalRevenue || 0,
    spareRevenue: data?.summary?.spareRevenue || 0,
    labourRevenue: data?.summary?.labourRevenue || 0,
    totalCOGS: data?.summary?.totalCOGS || 0,
    grossProfit: data?.summary?.grossProfit || 0,
    totalExpenses: data?.summary?.totalExpenses || 0,
    netProfit: data?.summary?.netProfit || 0
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Revenue Breakdown */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg text-gray-900 flex items-center border-b pb-2">
            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
            Income / Revenue
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between p-3 bg-green-50 rounded-lg">
              <span className="text-green-700 font-medium">Labour Revenue</span>
              <span className="text-green-900 font-bold">{currencySymbol}{summary.labourRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-blue-700 font-medium">Spare Sales Revenue</span>
              <span className="text-blue-900 font-bold">{currencySymbol}{summary.spareRevenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between p-4 bg-emerald-100 rounded-lg border border-emerald-200">
              <span className="emerald-800 font-black">Total Gross Revenue</span>
              <span className="emerald-900 font-black text-xl">{currencySymbol}{summary.totalRevenue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Expenses & COGS */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg text-gray-900 flex items-center border-b pb-2">
            <DollarSign className="w-5 h-5 mr-2 text-red-600" />
            Costs & Expenses
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between p-3 bg-orange-50 rounded-lg">
              <span className="text-orange-700 font-medium">COGS (Parts Cost)</span>
              <span className="text-orange-900 font-bold">{currencySymbol}{summary.totalCOGS.toFixed(2)}</span>
            </div>
            <div className="flex justify-between p-3 bg-red-50 rounded-lg">
              <span className="text-red-700 font-medium">Operating Expenses</span>
              <span className="text-red-900 font-bold">{currencySymbol}{summary.totalExpenses.toFixed(2)}</span>
            </div>
            <div className="flex justify-between p-4 bg-gray-100 rounded-lg border border-gray-200">
              <span className="text-gray-800 font-black">Total Outflow</span>
              <span className="text-gray-900 font-black text-xl">{currencySymbol}{(summary.totalCOGS + summary.totalExpenses).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Results */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t">
        <div className="p-6 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
          <p className="text-sm font-medium text-blue-600 mb-1 uppercase tracking-wider">Gross Margin</p>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-black text-blue-900">{currencySymbol}{summary.grossProfit.toFixed(2)}</span>
            <span className="text-sm text-blue-600">
              ({summary.totalRevenue > 0 ? ((summary.grossProfit / summary.totalRevenue) * 100).toFixed(1) : 0}%)
            </span>
          </div>
        </div>

        <div className={`p-6 rounded-xl border ${summary.netProfit >= 0 ? 'bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100' : 'bg-gradient-to-br from-red-50 to-orange-50 border-red-100'}`}>
          <p className={`text-sm font-medium mb-1 uppercase tracking-wider ${summary.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            Net {summary.netProfit >= 0 ? 'Profit' : 'Loss'}
          </p>
          <span className={`text-3xl font-black ${summary.netProfit >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
            {currencySymbol}{Math.abs(summary.netProfit).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Technician Report Component
const TechnicianReport = ({ data, currencySymbol }) => {
  const technicians = data?.technicians || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-cyan-50 p-4 rounded-lg">
          <p className="text-sm text-cyan-600 font-medium">Active Technicians</p>
          <p className="text-2xl font-bold text-cyan-900">{summary.totalTechnicians}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-blue-600 font-medium">Total Jobs Handled</p>
          <p className="text-2xl font-bold text-blue-900">{summary.totalJobs}</p>
        </div>
        <div className="bg-emerald-50 p-4 rounded-lg">
          <p className="text-sm text-emerald-600 font-medium">Total Tech Revenue</p>
          <p className="text-2xl font-bold text-emerald-900">{currencySymbol}{summary.totalRevenue.toFixed(2)}</p>
        </div>
      </div>

      {/* Technician Breakdown */}
      <div className="space-y-12">
        {technicians.map((tech, index) => (
          <div key={index} className="border rounded-xl overflow-hidden bg-white shadow-sm">
            <div className="bg-gray-50 px-6 py-4 border-b flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-cyan-600" />
                  {tech.name}
                </h3>
                <p className="text-sm text-gray-500">Technician Performance Summary</p>
              </div>
              <div className="grid grid-cols-2 md:flex md:space-x-4 gap-2">
                <div className="text-center px-3 py-1 bg-blue-100 rounded text-blue-800 text-xs font-bold">
                  Jobs: {tech.totalJobs}
                </div>
                <div className="text-center px-3 py-1 bg-green-100 rounded text-green-800 text-xs font-bold">
                  Done: {tech.completed}
                </div>
                <div className="text-center px-3 py-1 bg-orange-100 rounded text-orange-800 text-xs font-bold">
                  Wait: {tech.pending}
                </div>
                <div className="text-center px-3 py-1 bg-purple-100 rounded text-purple-800 text-xs font-bold">
                  {currencySymbol}{tech.totalRevenue.toFixed(0)}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tech.jobs.map((job, jIdx) => (
                    <tr key={jIdx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">{job.jobNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(job.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.customerName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{job.device}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          job.status === 'COMPLETED' || job.status === 'DELIVERED' ? 'bg-green-100 text-green-700' :
                          job.status === 'RETURNED' ? 'bg-red-100 text-red-700' :
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-right text-gray-900">
                        {currencySymbol}{job.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {technicians.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No technician data found for this period</p>
            <p className="text-sm text-gray-400">Try adjusting your filters or date range</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
